import {
  ClipboardWellDetails,
  CopyWellDetail,
  GridLayerDetailsDTO,
} from 'business/models/CustomModels';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { IngredientStockDTO, LayerDTO, LayerTypeDTO } from 'business/models/Models';
import { getIngredient } from 'business/plateviewer/Generate/GenerateCommon';
import {
  CutCopyOperationType,
  WellIng,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import { ClipboardBuffer } from 'components/plateviewer/experiment/ClipboardBuffer';
import { IndexDBBuffer } from 'components/plateviewer/experiment/IndexDBBuffer';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { cloneDeep } from 'lodash';
import { useCallback } from 'react';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  useAllWellNavigationDetails,
  useLayerDeleteHandler,
} from 'redux/hooks/experiment';
import { setIsLayerSelected } from 'redux/reducers/experimentDataReducer';
import {
  selectIsLayerSelected,
  selectSelectedLayer,
} from 'redux/selectors/ExperimentStateSelector';
import { getIngredientStock, GetWellsLabels, showAlertSnackBar } from 'utils/Global';

const BUFFER = new IndexDBBuffer();
const ClipBoardBuffer = new ClipboardBuffer();

export interface useCopyParams {
  setCopyType: (copyType: string) => void;
  setPasteDisable: (flag: boolean) => void;
  setLayerOperationType: (operationType: CutCopyOperationType) => void;
}
function useCopy(params) {
  const { setCopyType, setPasteDisable, setLayerOperationType } = params;
  const exp = useRMSelector((state) => state.experiment.expData);
  const isLayerSelected = useRMSelector(selectIsLayerSelected);
  const selectedLayer = useRMSelector(selectSelectedLayer);
  const selectedWells: WellNavigationDetails[] = useAllWellNavigationDetails();
  const isScreenRoute = window.location.pathname.includes('/screen');
  const dispatch = useRMDispatch();
  const handleDeleteLayer = useLayerDeleteHandler();

  const copyLayerHandler = useCallback(
    async (operationType: CutCopyOperationType) => {
      setLayerOperationType(operationType);
      if (!selectedLayer.locked) {
        const wells: CopyWellDetail[] = [];
        const clipboardDetails: ClipboardWellDetails[] = [];
        const wellLabels = GetWellsLabels();
        const plateWellList: any[] = [];
        const wellList: any[] = [];
        const newLayers: LayerDTO[] = [];
        let newLayer = new LayerDTO();
        const tempExp = cloneDeep(exp) as ExperimentDtoClient;
        newLayer = tempExp.layers.find(
          (lyr) => lyr.name === selectedLayer.name || lyr.id === selectedLayer.id,
        );
        newLayers.push(newLayer);
        if (newLayer.type === LayerTypeDTO.Grid) {
          const newGridId = (newLayer.layerDetail as GridLayerDetailsDTO).gridId;

          const matchingLayers = tempExp.layers.filter((layer) => {
            const layerDetail = layer.layerDetail as GridLayerDetailsDTO;
            return layerDetail.gridId === newGridId && layer.id !== newLayer.id;
          });

          newLayers.push(...matchingLayers);
        }
        tempExp.plateDto.forEach((plate) => {
          plate.ingredients.wellList.forEach((well) => {
            well.wellLayerList.findIndex((wll) => wll.layerId == selectedLayer.id);
            well['plateID'] = plate.plateId;
            plateWellList.push(well);
          });
        });

        if (
          selectedLayer.type === LayerTypeDTO.Protein ||
          selectedLayer.type === LayerTypeDTO.Fragment
        ) {
          plateWellList.forEach((m) => {
            m.dropList.forEach((s) => {
              s.dropLayerList.some((d) => {
                if (d.layerId === selectedLayer.id) {
                  wellList.push(m);
                }
              });
            });
          });
        } else {
          plateWellList.forEach((m) => {
            m.wellLayerList.forEach((s) => {
              if (s.layerId === selectedLayer.id) {
                wellList.push(m);
              }
            });
          });
        }
        wellList.forEach((wellDetail) => {
          const plate = tempExp.plateDto.find((p) => p.plateId == wellDetail.plateID);
          const well: WellIng = plate.ingredients.wellList.find(
            (w) => w.wellNumber == wellDetail.wellNumber,
          );
          const x = (well.wellNumber % tempExp.containerCols) - 1;
          const y = parseInt((well.wellNumber / tempExp.containerCols).toString());
          if (well.wellLayerList.find((wll) => wll.wellLayerIngredientList.length > 0)) {
            wells.push({ id: well.wellId, wellLayers: well.wellLayerList, x, y });
          }

          if (
            selectedLayer.type === LayerTypeDTO.Grid ||
            selectedLayer.type === LayerTypeDTO.Random ||
            selectedLayer.type === LayerTypeDTO.Screen
          ) {
            well.wellLayerList.forEach((wellLayer) => {
              wellLayer.wellLayerIngredientList.forEach((wellIng) => {
                const ingStock = getIngredientStock(wellIng.ingredientStockId);
                const ingredient = getIngredient(ingStock.ingredientId);
                let lowPHVolume = 0.0;
                let highPHVolume = 0.0;
                if (wellIng?.lowPHVolume) {
                  lowPHVolume = wellIng.lowPHVolume;
                }
                if (wellIng?.highPHVolume) {
                  highPHVolume = wellIng.highPHVolume;
                }

                if (!ingStock.isBuffer) {
                  const clipboardDetail: ClipboardWellDetails = {
                    plateBarcode: plate.barCode ?? '',
                    ingredient: ingredient.longName,
                    location: wellLabels[wellDetail.wellNumber - 1]?.name,
                    concentration: wellIng.concentration,
                    unit: ingStock.concentrationUnits,
                    volume: wellIng.volume,
                    buffer: ingStock.isBuffer ? 'High' : '',
                    ph: ingStock.isBuffer ? ingStock.stockPh : '',
                  };
                  clipboardDetails.push(clipboardDetail);
                } else {
                  const clipboardDetail: ClipboardWellDetails = {
                    plateBarcode: plate.barCode ?? '',
                    ingredient: ingredient.longName,
                    location: wellLabels[wellDetail.wellNumber - 1]?.name,
                    concentration: wellIng.concentration,
                    unit: ingStock.concentrationUnits,
                    volume: wellIng.volume,
                    buffer: '',
                    ph: '',
                  };
                  let highStockpH: IngredientStockDTO = null;
                  if (wellIng?.highPhIngStockId) {
                    highStockpH = getIngredientStock(wellIng.highPhIngStockId);
                  }
                  let stockPH = 0.0;
                  let stockHighPH = 0.0;
                  if (ingStock?.stockPh) {
                    stockPH = ingStock.stockPh;
                  }
                  if (highStockpH && highStockpH?.stockPh) {
                    stockHighPH = highStockpH.stockPh;
                  }

                  if (lowPHVolume > 0) {
                    clipboardDetail.ph = stockPH;
                    clipboardDetail.buffer = 'Low';
                    clipboardDetail.volume = lowPHVolume;

                    clipboardDetails.push({ ...clipboardDetail });
                  }
                  if (highPHVolume > 0) {
                    clipboardDetail.ph = stockHighPH;
                    clipboardDetail.buffer = 'High';
                    clipboardDetail.volume = highPHVolume;
                    clipboardDetails.push({ ...clipboardDetail });
                  }
                }
              });
            });
          } else if (
            selectedLayer.type === LayerTypeDTO.Protein ||
            selectedLayer.type === LayerTypeDTO.Fragment ||
            selectedLayer.type === LayerTypeDTO.Seed
          ) {
            const clipboardDetail: ClipboardWellDetails = {
              plateBarcode: plate.barCode ?? '',
              ingredient: '',
              location: wellLabels[wellDetail.wellNumber - 1]?.name,
              concentration: 0.0,
              unit: '',
              volume: 0.0,
              buffer: '',
              ph: '',
            };
            clipboardDetails.push(clipboardDetail);
          }
        });

        let documentType = 'Experiment';
        if (isScreenRoute) {
          documentType = 'Screen';
        }

        const copyType = [{ value: 'Layer', documentType: documentType }];
        await ClipBoardBuffer.write(clipboardDetails);
        await BUFFER.writeJson(null, newLayers, copyType);
        setPasteDisable(newLayers.length === 0);
        if (operationType === CutCopyOperationType.Cut) {
          handleDeleteLayer(selectedLayer.id, false, false);
          dispatch(setIsLayerSelected(false));
        }
        showAlertSnackBar(
          EAlertType.SUCCESS,
          ESnackBarPosition.TOP,
          `Layer has been ${
            operationType === CutCopyOperationType.Cut ? 'cut' : 'copied'
          }.`,
        );
      } else {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          `Cannot ${
            operationType === CutCopyOperationType.Cut ? 'cut' : 'copy'
          } locked layer.`,
        );
      }
    },
    [selectedLayer, exp, isScreenRoute, setPasteDisable],
  );

  const copyHandler = useCallback(
    async (operationType: CutCopyOperationType) => {
      if (!isLayerSelected && operationType === CutCopyOperationType.Copy) {
        setCopyType('Well');
        const wells: CopyWellDetail[] = [];
        const clipboardDetails: ClipboardWellDetails[] = [];
        const wellLabels = GetWellsLabels();
        const tempExp = cloneDeep(exp) as ExperimentDtoClient;
        selectedWells.forEach((wellDetail) => {
          const plate = tempExp.plateDto.find(
            (p) => p.plateNumber == wellDetail.plateNum,
          );
          const well: WellIng = plate.ingredients.wellList.find(
            (w) => w.wellNumber == wellDetail.wellNum,
          );

          const x = (well.wellNumber - 1) % tempExp.containerCols;
          const y = parseInt((well.wellNumber / tempExp.containerCols).toString());
          if (well.wellLayerList.find((wll) => wll.wellLayerIngredientList.length > 0)) {
            wells.push({ id: well.wellId, wellLayers: well.wellLayerList, x, y });
          }

          well.wellLayerList.forEach((wellLayer) => {
            wellLayer.wellLayerIngredientList.forEach((wellIng) => {
              const ingStock = getIngredientStock(wellIng.ingredientStockId);
              const ingredient = getIngredient(ingStock.ingredientId);
              let lowPHVolume = 0.0;
              let highPHVolume = 0.0;
              if (wellIng?.lowPHVolume) {
                lowPHVolume = wellIng.lowPHVolume;
              }
              if (wellIng?.highPHVolume) {
                highPHVolume = wellIng.highPHVolume;
              }

              if (!ingStock.isBuffer) {
                const clipboardDetail: ClipboardWellDetails = {
                  plateBarcode: plate.barCode ?? '',
                  ingredient: ingredient.longName,
                  location: wellLabels[wellDetail.wellNum - 1]?.name,
                  concentration: wellIng.concentration,
                  unit: ingStock.concentrationUnits,
                  volume: wellIng.volume,
                  buffer: ingStock.isBuffer ? 'High' : '',
                  ph: ingStock.isBuffer ? ingStock.stockPh : '',
                };
                clipboardDetails.push(clipboardDetail);
              } else {
                const clipboardDetail: ClipboardWellDetails = {
                  plateBarcode: plate.barCode ?? '',
                  ingredient: ingredient.longName,
                  location: wellLabels[wellDetail.wellNum - 1]?.name,
                  concentration: wellIng.concentration,
                  unit: ingStock.concentrationUnits,
                  volume: wellIng.volume,
                  buffer: '',
                  ph: '',
                };
                let highStockpH: IngredientStockDTO = null;
                if (wellIng?.highPhIngStockId) {
                  highStockpH = getIngredientStock(wellIng.highPhIngStockId);
                }
                let stockPH = 0.0;
                let stockHighPH = 0.0;
                if (ingStock?.stockPh) {
                  stockPH = ingStock?.stockPh;
                }
                if (highStockpH && highStockpH?.stockPh) {
                  stockHighPH = highStockpH.stockPh;
                }
                if (lowPHVolume > 0) {
                  clipboardDetail.ph = stockPH;
                  clipboardDetail.buffer = 'Low';
                  clipboardDetail.volume = lowPHVolume;

                  clipboardDetails.push({ ...clipboardDetail });
                }
                if (highPHVolume > 0) {
                  clipboardDetail.ph = stockHighPH;
                  clipboardDetail.buffer = 'High';
                  clipboardDetail.volume = highPHVolume;
                  clipboardDetails.push({ ...clipboardDetail });
                }
              }
            });
          });
        });

        let documentType = 'Experiment';
        if (isScreenRoute) {
          documentType = 'Screen';
        }

        const copyType = [{ value: 'Well', documentType: documentType }];
        BUFFER.writeJson(wells, null, copyType);
        ClipBoardBuffer.write(clipboardDetails);
        setPasteDisable(wells.length === 0);
        showAlertSnackBar(
          EAlertType.SUCCESS,
          ESnackBarPosition.TOP,
          'Well has been copied.',
        );
      } else {
        setCopyType('Layer');
        await copyLayerHandler(operationType);
      }
    },
    [
      isLayerSelected,
      setCopyType,
      exp?.plateDto,
      selectedWells,
      exp?.containerCols,
      exp?.containerRows,
      copyLayerHandler,
    ],
  );

  return copyHandler;
}

export default useCopy;
